import {
    Box,
    Button,
    Flex,
    Icon,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Spinner,
    Text,
    VStack,
} from '@chakra-ui/react';
import {
    TbBuilding,
    TbBox,
    TbChevronLeft,
    TbTag,
    TbTags,
    TbTruck,
} from 'react-icons/tb';
import type { IconType } from 'react-icons';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useGetLocations } from '../../../hooks/useGetLocations';
import { formatInventoryPartTypeLabel } from '../../../utils/inventoryConstants';
import type { InventoryPartType } from '../../../types/inventory';
import { AddFilterMenu, ClearFiltersButton, FilterBar, FilterChip } from '../../../ui';
import {
    CheckboxMenuRow,
    CheckboxMenuSearch,
    MenuRow,
    MenuFooterActions,
    checkboxMenuColors,
    checkboxMenuPopoverContentSx,
    checkboxMenuTriggerMetrics,
} from '../../../ui/primitives/CheckboxMenu';
import type {
    ReportingInventoryFilterSnapshot,
    ReportingInventoryFiltersModel,
} from './useReportingInventoryFilters';

type FilterType = 'location' | 'partType' | 'tags' | 'bins' | 'suppliers';
type OpenState = 'addFilter' | FilterType | null;

const FILTER_CATEGORIES: { key: FilterType; label: string; icon: IconType }[] = [
    { key: 'location', label: 'Location', icon: TbBuilding },
    { key: 'partType', label: 'Part Type', icon: TbTag },
    { key: 'tags', label: 'Tags', icon: TbTags },
    { key: 'bins', label: 'Bins', icon: TbBox },
    { key: 'suppliers', label: 'Suppliers', icon: TbTruck },
];

interface ReportingInventoryFiltersProps {
    filters: ReportingInventoryFiltersModel;
    /** When set (GM single-location), hide the Location filter dimension. */
    hideLocation?: boolean;
}

export default function ReportingInventoryFilters({
    filters,
    hideLocation = false,
}: ReportingInventoryFiltersProps) {
    const categories = useMemo(
        () => FILTER_CATEGORIES.filter((c) => !(hideLocation && c.key === 'location')),
        [hideLocation],
    );

    const [openState, setOpenState] = useState<OpenState>(null);
    const [addFilterView, setAddFilterView] = useState<FilterType | null>(null);
    const [temp, setTemp] = useState<ReportingInventoryFilterSnapshot | null>(null);
    const [filterSearch, setFilterSearch] = useState('');
    const [locationSearch, setLocationSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);

    const { data: locationsData, isFetching: isLocationsLoading } = useGetLocations();
    const locationOptions = useMemo(
        () =>
            (locationsData?.locations ?? [])
                .filter((l) => l._id !== 'all')
                .map((l) => ({ value: l._id, label: l.title })),
        [locationsData?.locations],
    );
    const locationLabelById = useMemo(() => {
        const map: Record<string, string> = {};
        for (const opt of locationOptions) map[opt.value] = opt.label;
        return map;
    }, [locationOptions]);
    const visibleLocationOptions = useMemo(() => {
        const q = locationSearch.trim().toLowerCase();
        if (!q) return locationOptions;
        return locationOptions.filter((opt) => opt.label.toLowerCase().includes(q));
    }, [locationOptions, locationSearch]);

    const closePopover = () => {
        setOpenState(null);
        setAddFilterView(null);
        setFilterSearch('');
        setLocationSearch('');
        setTemp(null);
        filters.setTagSearch('');
        filters.setBinSearch('');
        filters.setSupplierSearch('');
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!openState) return;
            const target = event.target as HTMLElement;
            if (containerRef.current?.contains(target)) return;
            if (target.closest('[data-filter-popover]')) return;
            closePopover();
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [openState]);

    const togglePopover = (target: OpenState) => {
        if (openState === target) {
            closePopover();
        } else {
            setTemp(filters.getSnapshot());
            setOpenState(target);
            setAddFilterView(null);
            setFilterSearch('');
            setLocationSearch('');
            filters.setTagSearch('');
            filters.setBinSearch('');
            filters.setSupplierSearch('');
        }
    };

    const applyFilters = () => {
        if (!temp) {
            closePopover();
            return;
        }
        filters.setSelectedLocations(temp.selectedLocations);
        filters.setSelectedPartTypes(temp.selectedPartTypes);
        filters.setSelectedTagIds(temp.selectedTagIds);
        filters.setSelectedBinIds(temp.selectedBinIds);
        filters.setSelectedSupplierIds(temp.selectedSupplierIds);
        filters.setTagLabelById(temp.tagLabelById);
        filters.setBinLabelById(temp.binLabelById);
        filters.setSupplierLabelById(temp.supplierLabelById);
        closePopover();
    };

    const clearFilter = (key: FilterType) => {
        if (key === 'location') {
            filters.setSelectedLocations(filters.locationId ? [filters.locationId] : []);
            filters.setSelectedBinIds([]);
            filters.setBinLabelById({});
        } else if (key === 'partType') {
            filters.setSelectedPartTypes([]);
        } else if (key === 'tags') {
            filters.setSelectedTagIds([]);
        } else if (key === 'bins') {
            filters.setSelectedBinIds([]);
        } else if (key === 'suppliers') {
            filters.setSelectedSupplierIds([]);
        }
    };

    const isFilterActive = (key: FilterType) => {
        if (key === 'location') {
            return (
                !hideLocation
                && filters.selectedLocations !== 'all'
                && filters.selectedLocations.length > 0
            );
        }
        if (key === 'partType') return filters.selectedPartTypes.length > 0;
        if (key === 'tags') return filters.selectedTagIds.length > 0;
        if (key === 'bins') return filters.selectedBinIds.length > 0;
        return filters.selectedSupplierIds.length > 0;
    };

    const formatMultiChip = (prefix: string, labels: string[]) =>
        `${prefix}: ${
            labels.length <= 2 ? labels.join(', ') : `${labels.length} selected`
        }`;

    const getChipLabel = (key: FilterType) => {
        if (key === 'location') {
            const locs = filters.selectedLocations;
            if (locs === 'all' || locs.length === 0) return 'Location: All';
            return formatMultiChip(
                'Location',
                locs.map((id) => locationLabelById[id] ?? id),
            );
        }
        if (key === 'partType') {
            return formatMultiChip(
                'Type',
                filters.selectedPartTypes.map(formatInventoryPartTypeLabel),
            );
        }
        if (key === 'tags') {
            return formatMultiChip(
                'Tags',
                filters.selectedTagIds.map((id) => filters.tagLabelById[id] ?? id),
            );
        }
        if (key === 'bins') {
            return formatMultiChip(
                'Bins',
                filters.selectedBinIds.map((id) => filters.binLabelById[id] ?? id),
            );
        }
        return formatMultiChip(
            'Suppliers',
            filters.selectedSupplierIds.map((id) => filters.supplierLabelById[id] ?? id),
        );
    };

    const getCategorySummary = (key: FilterType): string | undefined => {
        if (!isFilterActive(key)) return undefined;
        const label = getChipLabel(key);
        const separator = label.indexOf(': ');
        return separator === -1 ? label : label.slice(separator + 2);
    };

    const activeFilters = categories.filter((c) => isFilterActive(c.key));
    const filteredCategories = categories.filter((c) =>
        c.label.toLowerCase().includes(filterSearch.toLowerCase()),
    );

    const draft = temp ?? filters.getSnapshot();

    const togglePartType = (value: InventoryPartType) => {
        const current = draft.selectedPartTypes;
        const next = current.includes(value)
            ? current.filter((t) => t !== value)
            : [...current, value];
        setTemp({ ...draft, selectedPartTypes: next });
    };

    const toggleLocation = (value: string) => {
        const current =
            draft.selectedLocations === 'all' ? [] : draft.selectedLocations;
        const next = current.includes(value)
            ? current.filter((id) => id !== value)
            : [...current, value];
        // Bins are location-scoped — clear so stale bin IDs do not hide the new location.
        setTemp({
            ...draft,
            selectedLocations: next,
            selectedBinIds: [],
            binLabelById: {},
        });
    };

    const toggleTagged = (
        field: 'selectedTagIds' | 'selectedBinIds' | 'selectedSupplierIds',
        labelField: 'tagLabelById' | 'binLabelById' | 'supplierLabelById',
        value: string,
        label: string,
    ) => {
        const current = draft[field];
        const next = current.includes(value)
            ? current.filter((id) => id !== value)
            : [...current, value];
        setTemp({
            ...draft,
            [field]: next,
            [labelField]: { ...draft[labelField], [value]: label },
        });
    };

    const renderFilterEditor = (key: FilterType) => {
        if (key === 'location') {
            const selected =
                draft.selectedLocations === 'all' ? [] : draft.selectedLocations;
            return (
                <>
                    <CheckboxMenuSearch
                        value={locationSearch}
                        onChange={setLocationSearch}
                        placeholder="Search locations…"
                    />
                    {isLocationsLoading && (
                        <Flex justify="center" py={2}>
                            <Spinner size="sm" color={checkboxMenuColors.brand} />
                        </Flex>
                    )}
                    <VStack align="stretch" spacing={0} p="6px" maxH="200px" overflowY="auto">
                        {visibleLocationOptions.map((opt) => (
                            <CheckboxMenuRow
                                key={opt.value}
                                isSelected={selected.includes(opt.value)}
                                onClick={() => toggleLocation(opt.value)}
                            >
                                {opt.label}
                            </CheckboxMenuRow>
                        ))}
                        {!isLocationsLoading && visibleLocationOptions.length === 0 && (
                            <Text
                                fontSize={checkboxMenuTriggerMetrics.fontSize}
                                color={checkboxMenuColors.muted}
                                px="10px"
                                py="8px"
                            >
                                No locations found
                            </Text>
                        )}
                    </VStack>
                </>
            );
        }

        if (key === 'partType') {
            return (
                <VStack align="stretch" spacing={0} p="6px">
                    {filters.partTypeOptions.map((opt) => (
                        <CheckboxMenuRow
                            key={opt.value}
                            isSelected={draft.selectedPartTypes.includes(opt.value as InventoryPartType)}
                            onClick={() => togglePartType(opt.value as InventoryPartType)}
                        >
                            {opt.label}
                        </CheckboxMenuRow>
                    ))}
                </VStack>
            );
        }

        if (key === 'tags') {
            return (
                <>
                    <CheckboxMenuSearch
                        value={filters.tagSearch}
                        onChange={filters.setTagSearch}
                        placeholder="Search tags…"
                    />
                    {filters.isTagsLoading && (
                        <Flex justify="center" py={2}>
                            <Spinner size="sm" color={checkboxMenuColors.brand} />
                        </Flex>
                    )}
                    <VStack align="stretch" spacing={0} p="6px" maxH="200px" overflowY="auto">
                        {filters.tagOptions.map((opt) => (
                            <CheckboxMenuRow
                                key={opt.value}
                                isSelected={draft.selectedTagIds.includes(opt.value)}
                                onClick={() =>
                                    toggleTagged('selectedTagIds', 'tagLabelById', opt.value, opt.label)
                                }
                            >
                                {opt.label}
                            </CheckboxMenuRow>
                        ))}
                        {!filters.isTagsLoading && filters.tagOptions.length === 0 && (
                            <Text
                                fontSize={checkboxMenuTriggerMetrics.fontSize}
                                color={checkboxMenuColors.muted}
                                px="10px"
                                py="8px"
                            >
                                No tags found
                            </Text>
                        )}
                    </VStack>
                </>
            );
        }

        if (key === 'bins') {
            return (
                <>
                    <CheckboxMenuSearch
                        value={filters.binSearch}
                        onChange={filters.setBinSearch}
                        placeholder="Search bins…"
                    />
                    {filters.isBinsLoading && (
                        <Flex justify="center" py={2}>
                            <Spinner size="sm" color={checkboxMenuColors.brand} />
                        </Flex>
                    )}
                    <VStack align="stretch" spacing={0} p="6px" maxH="200px" overflowY="auto">
                        {filters.binOptions.map((opt) => (
                            <CheckboxMenuRow
                                key={opt.value}
                                isSelected={draft.selectedBinIds.includes(opt.value)}
                                onClick={() =>
                                    toggleTagged('selectedBinIds', 'binLabelById', opt.value, opt.label)
                                }
                            >
                                {opt.label}
                            </CheckboxMenuRow>
                        ))}
                        {!filters.isBinsLoading && filters.binOptions.length === 0 && (
                            <Text
                                fontSize={checkboxMenuTriggerMetrics.fontSize}
                                color={checkboxMenuColors.muted}
                                px="10px"
                                py="8px"
                            >
                                No bins found
                            </Text>
                        )}
                    </VStack>
                </>
            );
        }

        return (
            <>
                <CheckboxMenuSearch
                    value={filters.supplierSearch}
                    onChange={filters.setSupplierSearch}
                    placeholder="Search suppliers…"
                />
                {filters.isSuppliersLoading && (
                    <Flex justify="center" py={2}>
                        <Spinner size="sm" color={checkboxMenuColors.brand} />
                    </Flex>
                )}
                <VStack align="stretch" spacing={0} p="6px" maxH="200px" overflowY="auto">
                    {filters.supplierOptions.map((opt) => (
                        <CheckboxMenuRow
                            key={opt.value}
                            isSelected={draft.selectedSupplierIds.includes(opt.value)}
                            onClick={() =>
                                toggleTagged(
                                    'selectedSupplierIds',
                                    'supplierLabelById',
                                    opt.value,
                                    opt.label,
                                )
                            }
                        >
                            {opt.label}
                        </CheckboxMenuRow>
                    ))}
                    {!filters.isSuppliersLoading && filters.supplierOptions.length === 0 && (
                        <Text
                            fontSize={checkboxMenuTriggerMetrics.fontSize}
                            color={checkboxMenuColors.muted}
                            px="10px"
                            py="8px"
                        >
                            No suppliers found
                        </Text>
                    )}
                </VStack>
            </>
        );
    };

    const renderFooter = (showReset: boolean = true) => (
        <MenuFooterActions
            secondaryLabel={showReset ? 'Reset' : 'Cancel'}
            onSecondary={
                showReset
                    ? () =>
                        setTemp({
                            selectedLocations: filters.locationId ? [filters.locationId] : [],
                            selectedPartTypes: [],
                            selectedTagIds: [],
                            selectedBinIds: [],
                            selectedSupplierIds: [],
                            tagLabelById: draft.tagLabelById,
                            binLabelById: draft.binLabelById,
                            supplierLabelById: draft.supplierLabelById,
                        })
                    : closePopover
            }
            onApply={applyFilters}
        />
    );

    return (
        <Box ref={containerRef}>
            <FilterBar>
                {activeFilters.map((cat) => (
                    <Popover
                        key={cat.key}
                        isOpen={openState === cat.key}
                        placement="bottom-start"
                        closeOnBlur={false}
                    >
                        <PopoverTrigger>
                            <FilterChip
                                icon={<Icon as={cat.icon as React.ElementType} boxSize={4} />}
                                label={getChipLabel(cat.key)}
                                onRemove={() => clearFilter(cat.key)}
                                onClick={() => togglePopover(cat.key)}
                            />
                        </PopoverTrigger>
                        <PopoverContent
                            w="280px"
                            sx={checkboxMenuPopoverContentSx}
                            data-filter-popover
                        >
                            <PopoverBody p={0}>
                                {renderFilterEditor(cat.key)}
                                {renderFooter(false)}
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                ))}

                <AddFilterMenu
                    isOpen={openState === 'addFilter'}
                    onToggle={() => togglePopover('addFilter')}
                    width={addFilterView ? '280px' : '220px'}
                    contentProps={{ 'data-filter-popover': '' }}
                >
                    {addFilterView === null ? (
                        <>
                            <CheckboxMenuSearch
                                value={filterSearch}
                                onChange={setFilterSearch}
                                placeholder="Search filters..."
                            />
                            <VStack align="stretch" spacing={0} p="6px">
                                {filteredCategories.map((cat) => (
                                    <MenuRow
                                        key={cat.key}
                                        kind="drillIn"
                                        summary={getCategorySummary(cat.key)}
                                        icon={
                                            <Icon as={cat.icon as React.ElementType} boxSize={4} />
                                        }
                                        onClick={() => setAddFilterView(cat.key)}
                                    >
                                        {cat.label}
                                    </MenuRow>
                                ))}
                            </VStack>
                            {renderFooter(true)}
                        </>
                    ) : (
                        <>
                            <Box
                                px={2}
                                py={2}
                                borderBottom="1px solid"
                                borderColor="gray.100"
                            >
                                <Button
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => {
                                        setAddFilterView(null);
                                        setLocationSearch('');
                                        filters.setTagSearch('');
                                        filters.setBinSearch('');
                                        filters.setSupplierSearch('');
                                    }}
                                    leftIcon={
                                        <Icon as={TbChevronLeft as React.ElementType} />
                                    }
                                    color="gray.600"
                                    fontWeight="medium"
                                >
                                    {
                                        categories.find((c) => c.key === addFilterView)
                                            ?.label
                                    }
                                </Button>
                            </Box>
                            <Box p={0}>{renderFilterEditor(addFilterView)}</Box>
                            {renderFooter(true)}
                        </>
                    )}
                </AddFilterMenu>
                {activeFilters.length > 0 && (
                    <ClearFiltersButton onClick={filters.clearAllFilters} />
                )}
            </FilterBar>
        </Box>
    );
}
